import React, { lazy, Suspense, useEffect, useState } from "react";
import { Heading } from "./heading";
import { Subheading } from "./subheading";
import { cn } from "@/lib/utils";
import { GridLineHorizontal, GridLineVertical } from "./grid-lines";
import { useTranslation } from "react-i18next";

// Lazy load skeleton components to avoid loading them on initial render
const SkeletonOne = lazy(() => import("./skeletons/first").then(mod => ({ default: mod.SkeletonOne })));
const SkeletonTwo = lazy(() => import("./skeletons/second").then(mod => ({ default: mod.SkeletonTwo })));
const SkeletonThree = lazy(() => import("./skeletons/third").then(mod => ({ default: mod.SkeletonThree })));
const SkeletonFour = lazy(() => import("./skeletons/fourth").then(mod => ({ default: mod.SkeletonFour })));

export const Features = () => {
  const { t, i18n } = useTranslation('common');
  const [features, setFeatures] = useState([
    {
      id: "feature-1",
      title: "",
      description: "",
      skeleton: <SkeletonOne />,
      className: "col-span-1 md:col-span-4 border-b border-r dark:border-neutral-800",
    },
    {
      id: "feature-2",
      title: "",
      description: "",
      skeleton: <SkeletonTwo />,
      className: "border-b col-span-1 md:col-span-2 dark:border-neutral-800",
    },
    {
      id: "feature-3",
      title: "",
      description: "",
      skeleton: <SkeletonThree />,
      className: "col-span-1 md:col-span-3 border-r dark:border-neutral-800",
    },
    {
      id: "feature-4",
      title: "",
      description: "",
      skeleton: <SkeletonFour />,
      className: "col-span-1 md:col-span-3",
    },
  ]);
  
  // Update features when language changes
  useEffect(() => {
    setFeatures([
      {
        id: "feature-1",
        title: t('features.mainFeatures.items.0.title'),
        description: t('features.mainFeatures.items.0.description'),
        skeleton: <SkeletonOne />,
        className: "col-span-1 md:col-span-4 border-b border-r dark:border-neutral-800",
      },
      {
        id: "feature-2",
        title: t('features.mainFeatures.items.1.title'),
        description: t('features.mainFeatures.items.1.description'),
        skeleton: <SkeletonTwo />,
        className: "border-b col-span-1 md:col-span-2 dark:border-neutral-800",
      },
      {
        id: "feature-3",
        title: t('features.mainFeatures.items.2.title'),
        description: t('features.mainFeatures.items.2.description'),
        skeleton: <SkeletonThree />,
        className: "col-span-1 md:col-span-3 border-r dark:border-neutral-800",
      },
      {
        id: "feature-4",
        title: t('features.mainFeatures.items.3.title'),
        description: t('features.mainFeatures.items.3.description'),
        skeleton: <SkeletonFour />,
        className: "col-span-1 md:col-span-3",
      },
    ]);
  }, [t, i18n.language]);
  
  // Listen for language change events
  useEffect(() => {
    const handleLanguageChange = () => {
      setFeatures([
        {
          id: "feature-1",
          title: t('features.mainFeatures.items.0.title'),
          description: t('features.mainFeatures.items.0.description'),
          skeleton: <SkeletonOne />,
          className: "col-span-1 md:col-span-4 border-b border-r dark:border-neutral-800",
        },
        {
          id: "feature-2",
          title: t('features.mainFeatures.items.1.title'),
          description: t('features.mainFeatures.items.1.description'),
          skeleton: <SkeletonTwo />,
          className: "border-b col-span-1 md:col-span-2 dark:border-neutral-800",
        },
        {
          id: "feature-3",
          title: t('features.mainFeatures.items.2.title'),
          description: t('features.mainFeatures.items.2.description'),
          skeleton: <SkeletonThree />,
          className: "col-span-1 md:col-span-3 border-r dark:border-neutral-800",
        },
        {
          id: "feature-4",
          title: t('features.mainFeatures.items.3.title'),
          description: t('features.mainFeatures.items.3.description'),
          skeleton: <SkeletonFour />,
          className: "col-span-1 md:col-span-3",
        },
      ]);
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [t]);
  
  return (
    <div className="relative z-20 py-10 md:py-20">
      <Heading as="h2">{t('features.mainFeatures.title')}</Heading>
      <Subheading className="text-center ">
        {t('features.mainFeatures.subtitle')}
      </Subheading>

      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-6 mt-12">
          {features.map((feature) => (
            <FeatureCard key={feature.id} className={feature.className}>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
              <div className=" h-full w-full">
                <Suspense fallback={<div className="h-40 bg-gray-100 dark:bg-gray-800 rounded-md animate-pulse"></div>}>
                  {feature.skeleton}
                </Suspense>
              </div>
            </FeatureCard>
          ))}
        </div>
        <GridLineHorizontal
          style={{
            top: 0,
            left: "-10%",
            width: "120%",
          }}
        />

        <GridLineHorizontal
          style={{
            bottom: 0,
            left: "-10%",
            width: "120%",
          }}
        />

        <GridLineVertical
          style={{
            top: "-10%",
            right: 0,
            height: "120%",
          }}
        />
        <GridLineVertical
          style={{
            top: "-10%",
            left: 0,
            height: "120%",
          }}
        />
      </div>
    </div>
  );
};

const FeatureCard = ({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn(`p-4 sm:p-8 relative overflow-hidden`, className)}>
      {children}
    </div>
  );
};

const FeatureTitle = ({ children }: { children?: React.ReactNode }) => {
  return (
    <Heading as="h3" size="sm" className="text-left">
      {children}
    </Heading>
  );
};

const FeatureDescription = ({ children }: { children?: React.ReactNode }) => {
  return (
    <Subheading className="text-left  mx-0 text-base leading-7 my-2">
      {children}
    </Subheading>
  );
};
